import { useForm } from '@tanstack/react-form'
import { useState, useEffect } from 'react'
import {
  cbpgCalculatorSchema,
  type CBPGCalculatorFormData,
} from './cbpg-calculator.schema'
import { useCalculateTaxCBPG } from '@/modules/hs/hooks/useCalculateTaxCBPG'
import type { HsTaxCBPGCalculatorRequest } from '@/modules/hs/types/hs-tax.response'

interface UseCBPGCalculatorFormProps {
  hsCode: string
  onSuccess?: () => void
}

/**
 * Custom hook quản lý CBPG Calculator Form
 * - Sử dụng @tanstack/react-form cho state management
 * - Tích hợp API call với React Query
 */
export function useCBPGCalculatorForm({
  hsCode,
}: UseCBPGCalculatorFormProps) {
  const { mutate, data, isPending, error, reset } = useCalculateTaxCBPG()

  // Chỉ hiển thị loading nếu request > 300ms để tránh flash
  const [showLoading, setShowLoading] = useState(false)

  useEffect(() => {
    if (isPending) {
      // Đợi 300ms trước khi hiển thị loading
      const timer = setTimeout(() => {
        setShowLoading(true)
      }, 300)
      return () => clearTimeout(timer)
    } else {
      // Ẩn loading ngay lập tức khi hoàn thành
      setShowLoading(false)
    }
  }, [isPending])

  const form = useForm({
    defaultValues: {
      importCountry: '',
      importDate: '2025-01-01',
      quantity: 10,
      unitPrice: 10,
      currency: 'USD',
      formCOCountry: undefined,
      manufactoringCompany: undefined,
      tradingCompany: undefined,
    } as CBPGCalculatorFormData,
    onSubmit: async ({ value }) => {
      // Reset previous result
      reset()

      const manufactoringCompany =
        value.manufactoringCompany === 'Other companies'
          ? undefined
          : value.manufactoringCompany
      const tradingCompany =
        value.tradingCompany === 'Other companies'
          ? undefined
          : value.tradingCompany

      // Prepare request data
      const requestData: HsTaxCBPGCalculatorRequest = {
        hsCode,
        importDate: value.importDate,
        formCOCountry: value.formCOCountry,
        manufactoringCompany,
        tradingCompany,
      }

      mutate(requestData)
    },
  })

  return {
    form,
    result: data,
    isPending: showLoading, // Sử dụng showLoading thay vì isPending trực tiếp
    error,
    reset,
  }
}
